// src/components/layout/MainLayout.js
import React, { useState } from 'react';
import Sidebar from './Sidebar';
import { Menu, Bell, RotateCcw } from 'lucide-react';
import NotificationsPanel from '../common/NotificationsPanel';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../../firebase/config';

const Header = ({ onLogout, user, onToggleMobileMenu, onToggleNotifications, hasUnreadNotifications, currentUserProfile }) => {
    
    const isOwner = ['Owner', 'Owner/System Admin', 'System Admin', 'Admin'].includes(currentUserProfile?.role);

    const handleRestartOnboarding = async () => {
        if (!currentUserProfile?.id) return;
        try {
            await updateDoc(doc(db, 'users', currentUserProfile.id), {
                onboardingCompleted: false
            });
            // Navigate to home so the widget appears immediately
            window.location.href = '/home';
        } catch (err) {
            console.error('Failed to restart onboarding:', err);
        }
    };

    return (
        <header className="bg-white border-b border-gray-200 p-3 md:p-4 flex justify-between items-center z-10 sticky top-0 md:relative">
            {/* Mobile Menu Button */}
            <button onClick={onToggleMobileMenu} className="text-gray-600 md:hidden p-1 -ml-1 hover:bg-gray-100 rounded-md">
                <Menu size={28} />
            </button>
            
            <div className="hidden md:block" />
            
            <div className="flex items-center space-x-2 md:space-x-4">
                <span className="hidden sm:block text-gray-600 text-sm truncate max-w-[150px]">
                  {user?.email}
                </span>

                {/* Restart setup guide — only shown to owners who have completed onboarding */}
                {isOwner && currentUserProfile?.onboardingCompleted && (
                    <button
                        onClick={handleRestartOnboarding}
                        className="hidden sm:flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-md transition-colors"
                        title="Restart setup guide"
                    >
                        <RotateCcw size={13} />
                        Setup guide
                    </button>
                )}
                
                <button
                    onClick={onToggleNotifications}
                    className="relative p-2 text-gray-600 rounded-full hover:bg-gray-100"
                    title="Notifications"
                >
                    <Bell size={24} />
                    {hasUnreadNotifications && (
                        <span className="absolute top-1.5 right-1.5 block h-2.5 w-2.5 rounded-full bg-red-500 ring-2 ring-white"></span>
                    )}
                </button>
                
                <button
                  onClick={onLogout}
                  className="px-3 py-1.5 md:py-2 bg-red-600 text-white rounded text-xs md:text-sm font-semibold hover:bg-red-700 whitespace-nowrap"
                >
                  Log Out
                </button>
            </div>
        </header>
    );
};

// PHASE 1.4 VERIFIER: r1-8d3c1e47
// onMarkFlagDone prop removed (Mark Done feature nuked in Batch 2.8).
// PHASE 1.6 VERIFIER: r1-16-web-sidebar-badge-16a3f2b8
// newFlaggedCount prop added — drives the red pill on the Flagged sidebar entry.
const MainLayout = ({ children, user, currentUserProfile, onLogout, notifications, onNotificationClick, newLeadCount, newFlaggedCount, onMarkAllRead }) => {
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const [isNotificationsOpen, setIsNotificationsOpen] = useState(false);
    
    const hasUnread = notifications && notifications.some(n => !n.isRead);

    return (
        <div className="flex h-screen bg-gray-50 overflow-hidden">
            <Sidebar 
                isMobileMenuOpen={isMobileMenuOpen} 
                closeMobileMenu={() => setIsMobileMenuOpen(false)} 
                currentUserProfile={currentUserProfile} 
                newLeadCount={newLeadCount}
                newFlaggedCount={newFlaggedCount}
            />
            
            <div className="flex-1 flex flex-col md:ml-64 min-w-0 relative">
                <Header 
                    onLogout={onLogout}
                    user={user}
                    currentUserProfile={currentUserProfile}
                    onToggleMobileMenu={() => setIsMobileMenuOpen(prev => !prev)}
                    onToggleNotifications={() => setIsNotificationsOpen(prev => !prev)}
                    hasUnreadNotifications={hasUnread}
                />
                
                {isNotificationsOpen && (
                    <div className="absolute right-0 top-14 z-50 w-full md:w-80 px-2 md:px-0">
                        <NotificationsPanel 
                            notifications={notifications} 
                            onNotificationClick={(n) => {
                                onNotificationClick(n);
                                setIsNotificationsOpen(false); 
                            }}
                            onMarkAllRead={onMarkAllRead} 
                            onClose={() => setIsNotificationsOpen(false)}
                        />
                    </div>
                )}

                <main className="flex-1 overflow-y-auto overflow-x-hidden w-full">
                    {children}
                </main>
            </div>
        </div>
    );
};

export default MainLayout;